import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
  Link,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Close as CloseIcon,
  InfoOutlined,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../../helpers/constants';

export interface OdooConnectionData {
  odooUrl: string;
  databaseName: string;
  username: string;
  apiKey: string;
}

export interface OdooDialogProps {
  open: boolean;
  appName: string;
  onClose: () => void;
  onConnect: (connectionData: OdooConnectionData) => Promise<void> | void;
}

const OdooConnectionDialog: React.FC<OdooDialogProps> = ({
  open,
  appName,
  onClose,
  onConnect,
}) => {
  const [odooUrl, setOdooUrl] = useState('');
  const [databaseName, setDatabaseName] = useState('');
  const [username, setUsername] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isConnecting, setIsConnecting] = useState(false);
  // Odoo database discovery (db.list() via backend). null = show free-text field;
  // a non-empty array = show a dropdown of the discovered databases.
  const [dbOptions, setDbOptions] = useState<string[] | null>(null);
  const [loadingDbs, setLoadingDbs] = useState(false);
  // Monotonic id of the latest discovery request — responses whose id no longer
  // matches are stale (a newer blur/URL fired) and are discarded.
  const reqIdRef = React.useRef(0);
  // The URL we last got a definitive answer for — skip re-fetching on repeated
  // blurs of the same value (avoids hammering the backend's per-user rate limit).
  const lastFetchedUrlRef = React.useRef('');
  // Guards against setState after the dialog unmounts mid-fetch.
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  React.useEffect(() => {
    if (open) {
      setOdooUrl('');
      setDatabaseName('');
      setUsername('');
      setApiKey('');
      setErrors({});
      setIsConnecting(false);
      setShowApiKey(false);
      setDbOptions(null);
      setLoadingDbs(false);
      reqIdRef.current += 1;      // invalidate any in-flight request from a prior open
      lastFetchedUrlRef.current = '';
    }
  }, [open]);

  // Ask the backend which databases this Odoo instance exposes (best-effort).
  // The browser can't call Odoo directly (CORS), so the backend runs db.list().
  // ≥2 results → dropdown; exactly 1 → auto-fill; 0/blocked → keep free text.
  const fetchDatabases = async () => {
    const url = odooUrl.trim();
    // Nothing to do, or we already have a definitive answer for this exact URL.
    if (!url || url === lastFetchedUrlRef.current) return;

    const reqId = ++reqIdRef.current;
    // True only if this request is still the newest and the component is mounted.
    const isCurrent = () => isMountedRef.current && reqId === reqIdRef.current;

    setLoadingDbs(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.ODOO_DATABASES}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ odoo_url: url }),
      });
      if (!isCurrent()) return; // superseded by a newer request / unmounted
      if (!response.ok) {
        // Definitive "listing unavailable" (disabled/blocked) — cache and fall back.
        lastFetchedUrlRef.current = url;
        setDbOptions(null);
        return;
      }
      const data = await response.json();
      if (!isCurrent()) return; // re-check: another request may have started during await
      lastFetchedUrlRef.current = url;
      const databases: string[] = Array.isArray(data?.databases) ? data.databases : [];
      if (databases.length >= 2) {
        setDbOptions(databases);
        // Only default the selection if the current value isn't already valid —
        // never clobber a value the user picked/typed.
        if (!databases.includes(databaseName)) {
          setDatabaseName(databases[0]);
        }
        clearFieldError('databaseName');
      } else if (databases.length === 1) {
        setDbOptions(null);
        // Same non-destructive guard: don't overwrite a manual correction.
        if (!databases.includes(databaseName)) {
          setDatabaseName(databases[0]);
        }
        clearFieldError('databaseName');
      } else {
        setDbOptions(null);
      }
    } catch {
      // Transient error (network/CORS) — fall back to manual entry and allow a
      // retry on the next blur (lastFetchedUrlRef intentionally left unset).
      if (isCurrent()) setDbOptions(null);
    } finally {
      if (isCurrent()) setLoadingDbs(false);
    }
  };

  const clearFieldError = (field: string) => {
    if (!errors[field]) return;
    setErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!odooUrl.trim()) {
      nextErrors.odooUrl = 'Odoo URL is required';
    } else {
      try {
        const parsedUrl = new URL(odooUrl.trim());
        if (!parsedUrl.protocol.startsWith('http')) {
          nextErrors.odooUrl = 'Please enter a valid Odoo URL (e.g., https://xxxx.odoo.com)';
        }
      } catch {
        nextErrors.odooUrl = 'Please enter a valid Odoo URL (e.g., https://xxxx.odoo.com)';
      }
    }

    if (!databaseName.trim()) {
      nextErrors.databaseName = 'Database name is required';
    }

    if (!username.trim()) {
      nextErrors.username = 'Username is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(username.trim())) {
        nextErrors.username = 'Please enter a valid email address for username';
      }
    }

    if (!apiKey.trim()) {
      nextErrors.apiKey = 'API key is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleConnect = async () => {
    if (!validateForm()) return;
    setIsConnecting(true);
    try {
      await onConnect({
        odooUrl: odooUrl.trim(),
        databaseName: databaseName.trim(),
        username: username.trim(),
        apiKey: apiKey.trim(),
      });
    } catch (error) {
      console.error('Error connecting Odoo:', error);
      setErrors({ general: 'Failed to connect. Please check your credentials and try again.' });
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Connect {appName}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {errors.general && <Alert severity="error">{errors.general}</Alert>}

          <TextField
            label="Odoo Dashboard URL"
            placeholder="https://xxxx.odoo.com"
            value={odooUrl}
            onChange={(e) => {
              setOdooUrl(e.target.value);
              clearFieldError('odooUrl');
              // URL changed → any previously discovered list is stale, and the
              // new value is eligible to be looked up again on the next blur.
              if (dbOptions !== null) setDbOptions(null);
              lastFetchedUrlRef.current = '';
            }}
            onBlur={fetchDatabases}
            error={!!errors.odooUrl}
            helperText={errors.odooUrl || 'Your specific subdomain URL (e.g., https://xxxx.odoo.com)'}
            fullWidth
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    placement="top"
                    title={
                      <Box sx={{ maxWidth: 360, p: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Odoo Dashboard URL</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          <strong>Guidance:</strong> This is the base address you use to log in to your Odoo instance.
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          <strong>How to find it:</strong> Open Odoo in your browser and copy the address (e.g., https://yourcompany.odoo.com).
                        </Typography>
                        <Link
                          href="https://www.odoo.com/documentation/18.0/administration/maintain/odoo_online.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="always"
                          sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.75, color: 'inherit' }}
                        >
                          Odoo Online Access Guide <OpenInNewIcon sx={{ ml: 0.5, fontSize: 14 }} />
                        </Link>
                      </Box>
                    }
                  >
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {dbOptions && dbOptions.length > 0 ? (
            // Databases discovered on the instance — let the user pick instead of typing.
            <TextField
              select
              label="Database Name"
              value={databaseName}
              onChange={(e) => {
                setDatabaseName(e.target.value);
                clearFieldError('databaseName');
              }}
              error={!!errors.databaseName}
              helperText={errors.databaseName || 'Detected on your Odoo instance — pick the database to connect.'}
              fullWidth
              required
            >
              {dbOptions.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="Database Name"
              placeholder="xxxx"
              value={databaseName}
              onChange={(e) => {
                setDatabaseName(e.target.value);
                clearFieldError('databaseName');
              }}
              error={!!errors.databaseName}
              helperText={
                errors.databaseName ||
                (loadingDbs
                  ? 'Detecting databases…'
                  : 'The db parameter in the subdomain URL (e.g., .../web?db=my_database)')
              }
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loadingDbs && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                    <Tooltip
                      placement="top"
                      title={
                        <Box sx={{ maxWidth: 360, p: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Database Name</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            <strong>Guidance:</strong> The specific internal name of your database.
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            <strong>How to find it:</strong>
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                            - Look at your URL; it often appears after db= (e.g., .../web?db=my_database).
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                            - Alternatively, go to Settings and activate Developer Mode. Your database name appears in parentheses next to your username in the top right corner.
                          </Typography>
                          <Link
                            href="https://www.odoo.com/documentation/18.0/developer/reference/external_api.html#logging-in"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="always"
                            sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.75, color: 'inherit' }}
                          >
                            Finding your Database Name <OpenInNewIcon sx={{ ml: 0.5, fontSize: 14 }} />
                          </Link>
                        </Box>
                      }
                    >
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <TextField
            label="Username (Email)"
            placeholder="user@example.com"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              clearFieldError('username');
            }}
            error={!!errors.username}
            helperText={errors.username || 'Your Odoo login email address'}
            fullWidth
            required
          />

          <TextField
            label="API Key"
            type={showApiKey ? 'text' : 'password'}
            placeholder="aa82-xxxx-xxxx"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              clearFieldError('apiKey');
            }}
            error={!!errors.apiKey}
            helperText={errors.apiKey || 'The key you generated in Profile > Security'}
            fullWidth
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    placement="top"
                    title={
                      <Box sx={{ maxWidth: 360, p: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>API Key</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          <strong>Guidance:</strong> A secure "guest password" that lets your app talk to Odoo without using your master password.
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          <strong>How to find it:</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                          1. Click your User Avatar (top right) - My Profile.
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                          2. Go to the Account Security tab.
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                          3. Click New API Key, enter your password, and copy the key.
                        </Typography>
                        <Link
                          href="https://www.odoo.com/documentation/18.0/applications/general/users.html#api-keys"
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="always"
                          sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.75, color: 'inherit' }}
                        >
                          How to generate API Keys <OpenInNewIcon sx={{ ml: 0.5, fontSize: 14 }} />
                        </Link>
                      </Box>
                    }
                  >
                    <InfoOutlined fontSize="small" color="action" sx={{ mr: 0.5 }} />
                  </Tooltip>
                  <IconButton
                    onClick={() => setShowApiKey(prev => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isConnecting}>
          Cancel
        </Button>
        <Button onClick={handleConnect} variant="contained" disabled={isConnecting}>
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OdooConnectionDialog;
